
import CertificateModal from "@/components/modal/Certificate.vue";
import ConfirmModal from "@/components/modal/ConfirmModal.vue";
import DisplayCertificate from "@/components/modal/DisplayCertificate.vue";
import ErrorModal from "@/components/modal/Error.vue";
import PaymentModal from "@/components/modal/PaymentModal.vue";
import SuccessModal from "@/components/modal/Success.vue";
import TeamAdminModal, {
  TeamAdminModalType,
} from "@/components/modal/TeamAdminModal.vue";
import VaModal from "@/components/modal/VA.vue";
import MiniTopBar from "@/components/topBar/MiniTopBar.vue";
import TopBar from "@/components/topBar/TopBar.vue";
import { dateFormat } from "@/types/dateFormat";
import { Inscription, InscriptionStatus, Race } from "@/types/interface";
import { PaymentStatus } from "@/types/payment";
import { Athlete, Member, Team } from "@/types/team";
import axios from "axios";
import { defineComponent } from "vue";

export default defineComponent({
  components: {
    TopBar,
    MiniTopBar,
    CertificateModal,
    VaModal,
    ErrorModal,
    SuccessModal,
    TeamAdminModal,
    ConfirmModal,
    DisplayCertificate,
    PaymentModal,
  },
  data() {
    return {
      title: "Dashboard",
      successPasswordUpdate: false,
      showTeamSettings: false,
      showCertificateModal: false,
      showErrorModal: false,
      showSuccessModal: false,
      successMessage: undefined as string | undefined,
      showVaModal: false,
      showPaymentModal: false,
      showTeamAdminModal: false,
      showConfirmModal: false,
      confirmMessage: undefined as string | undefined,
      confirmCallback: () => {
        return;
      },
      showDisplayCertificateModal: false,
      certificateFile: "",
      newTeamPassword: "",
      confirmTeamPassword: "",
      matchError: false,
      lengthError: false,
      team: {} as Team,
      athlete: {} as Athlete,
      TeamAdminModalType,
      actionType: "" as TeamAdminModalType,
      mailTemplate: "",
      PaymentStatus,
      InscriptionStatus,
      race: {} as Race,
    };
  },
  computed: {
    me() {
      return this.$store.getters["user/getMe"];
    },
    inscription() {
      return this.$store.getters["user/getMe"].inscriptions?.filter(
        (inscription: Inscription) =>
          inscription.edition.id ===
            this.$store.getters["edition/getEditionId"] &&
          inscription.status !== InscriptionStatus.CANCELLED
      )[0];
    },
    isRegistrationOpen() {
      return this.$store.getters["edition/isRegistrationOpen"];
    },
    getRegistrationStartDate() {
      return this.$store.getters["edition/getRegistrationStartDate"].toLocaleDateString("FR-fr", dateFormat);;
    },
    getRegistrationEndDate() {
      return this.$store.getters["edition/getRegistrationEndDate"].toLocaleDateString("FR-fr", dateFormat);;
    }
  },
  methods: {
    register() {
      this.$router.push({ name: "RegisterTeam" });
    },
    centimesToEuros(price: number) {
      return price / 100;
    },
    toggleTeamSettings() {
      this.newTeamPassword = "";
      this.confirmTeamPassword = "";
      this.matchError = false;
      this.lengthError = false;
      this.successPasswordUpdate = false;
      this.showTeamSettings = !this.showTeamSettings;
    },
    toggleCertificateModal() {
      this.showCertificateModal = !this.showCertificateModal;
    },
    toggleVaModal() {
      this.showVaModal = !this.showVaModal;
    },
    togglePaymentModal() {
      this.showPaymentModal = !this.showPaymentModal;
    },
    toggleErrorModal() {
      this.showErrorModal = !this.showErrorModal;
    },
    toggleSuccessModal() {
      this.showSuccessModal = !this.showSuccessModal;
    },
    toggleTeamAdminModal() {
      this.showTeamAdminModal = !this.showTeamAdminModal;
    },
    openTeamAdminModal(athlete: Athlete, action: TeamAdminModalType) {
      this.actionType = action;
      this.athlete = athlete;
      this.toggleTeamAdminModal();
    },
    toggleConfirmModal() {
      this.showConfirmModal = !this.showConfirmModal;
    },
    toggleDisplayCertificateModal() {
      this.showDisplayCertificateModal = !this.showDisplayCertificateModal;
    },
    openCertificateModal(certificateFile: string) {
      this.certificateFile = certificateFile;
      this.toggleDisplayCertificateModal();
    },
    async openLeaveTeamModal() {
      this.confirmMessage = "Voulez-vous vraiment quitter l'équipe ?";
      this.confirmCallback = await this.leaveTeam;
      this.toggleConfirmModal();
    },
    checkPassword() {
      let error = false;
      if (this.newTeamPassword !== this.confirmTeamPassword) {
        error = true;
        this.matchError = true;
      }
      if (this.newTeamPassword.length <= 8) {
        error = true;
        this.lengthError = true;
      }
      return !error;
    },
    async getRaceDetails() {
      if (!this.inscription?.race) return;
      const raceResponse = await axios.get(
        `/races/${this.inscription.race.id}`
      );
      if (raceResponse.status === 200) {
        this.race = raceResponse.data;
      }
    },
    async getTeamInfos() {
      if (!this.inscription?.team) return;
      const teamResponse = await axios.get(
        `/teams/${this.inscription.team.id}`
      );
      if (teamResponse.status === 200) {
        this.team = teamResponse.data;
        this.isTeamAdmin(this.me.id);
      }
    },
    isTeamAdmin(athleteId: number) {
      return this.team?.admins?.some(
        (admin) => admin.adminInscription.athleteId === athleteId
      );
    },
    async submitChangeTeamPassword() {
      if (!this.checkPassword()) return;
      const changeTeamPasswordResponse = await axios.post(
        `/teams/${this.team.id}/updatePassword`,
        {
          password: this.newTeamPassword,
        }
      );
      if (changeTeamPasswordResponse.status !== 200)
        return this.toggleErrorModal();

      this.newTeamPassword = "";
      this.confirmTeamPassword = "";

      this.successMessage = "Mot de passe changé avec succès";
      this.successPasswordUpdate = true;
    },
    async leaveTeam() {
      const leaveTeamResponse = await axios.post(
        `/teams/${this.team.id}/leave`,
        {
          athleteId: this.me.id,
        }
      );
      if (leaveTeamResponse.status !== 200) return this.toggleErrorModal();

      this.successMessage = "Vous avez quitté l'équipe";
      this.toggleSuccessModal();
      this.$store.dispatch("user/setMe");
    },
    fillMailTemplate() {
      this.mailTemplate = `Bonjour,
Je souhaite modifier mon inscription.
Voici mes informations:
- Pseudo: ${this.me.pseudo}
- Nom: ${this.me.lastName}
- Prénom: ${this.me.firstName}

[Merci de compléter ce mail avec les raisons de la modification 
(changer de course, d'équipe, retirer un membre de l'équipe,...)]

Merci d'avance pour votre aide.
      `;
    },
    mailURL() {
      return `mailto:overrun@24heures.org?subject=${encodeURI(
        "Modification d'une inscription"
      )}&body=${encodeURI(this.mailTemplate)}`;
    },
    teamMembersWithoutCanceledInscription() {
      return this.team.members.filter(
        (m: Member) => m.status !== InscriptionStatus.CANCELLED
      );
    },
    myInscriptionWithoutCanceledPayment() {
      return this.me.inscriptions?.filter(
        (i: Inscription) =>
          i.payment &&
          (i.payment.status === PaymentStatus.VALIDATED ||
            i.payment.status === PaymentStatus.REFUNDING ||
            i.payment.status === PaymentStatus.REFUND)
      );
    },
    myInscriptionWithCertificate() {
      return this.me.inscriptions?.filter((i: Inscription) => i.certificate);
    },
  },
  watch: {
    inscription() {
      this.getTeamInfos();
    },
    newTeamPassword() {
      this.lengthError = false;
      this.matchError = false;
    },
    confirmTeamPassword() {
      this.lengthError = false;
      this.matchError = false;
    },
  },
  mounted() {
    this.fillMailTemplate();
    this.$store.dispatch("user/setMe");
    // get disciplines details based on inscription raceId
    this.getTeamInfos();
    this.getRaceDetails();
  },
});
